"use client";

import { buttonVariants } from "@repo/ui/components/ui/button";
import { QuantityStepper } from "@repo/ui/components/ui/stepper";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { addToCart, getProductPrice, type PriceBasis, type ProductPrice } from "../../../../../lib/api-client";
import { priceLabel } from "../../../../../lib/product-display";
import { useSecondsAgo } from "../../../../../lib/use-live-prices";

const POLL_MS = 30_000;

// The bar's live price box (plan 2.13 §5; the breakdown card was dropped 2026-10-13). Server HTML
// carries the real price (Google needs it to match the JSON-LD); this polls
// /api/prices/product/:id and patches it in place with a brief highlight.
// Add-to-cart puts the bar in the public cart (2.6); it's disabled only when
// the bar is out of stock or its price is halted (F17).
export function PricePanel({
  productId,
  initial,
  available,
}: {
  productId: string;
  initial: { price: ProductPrice; basis: PriceBasis | null; updatedAt: string | null };
  available: number;
}) {
  const [live, setLive] = useState(initial);
  const [flash, setFlash] = useState(false);
  const lastPrice = useRef(initial.price.priceRial);
  const updatedAgo = useSecondsAgo(live.updatedAt);

  useEffect(() => {
    let cancelled = false;
    const timer = setInterval(async () => {
      const { status, data } = await getProductPrice(productId).catch(() => ({ status: 0, data: null }));
      if (cancelled || status !== 200 || !data) return;
      if (lastPrice.current !== data.price.priceRial) {
        lastPrice.current = data.price.priceRial;
        setFlash(true);
        setTimeout(() => setFlash(false), 2_000);
      }
      setLive({ price: data.price, basis: data.basis, updatedAt: data.updatedAt });
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [productId]);

  const [quantity, setQuantity] = useState(1);
  const [cartState, setCartState] = useState<"idle" | "adding" | "added" | "error">("idle");

  const { price } = live;
  const inStock = available > 0;
  const canAdd = inStock && !price.halted && cartState !== "adding";

  const add = async () => {
    setCartState("adding");
    const { status } = await addToCart(productId, quantity).catch(() => ({ status: 0 }));
    setCartState(status === 201 ? "added" : "error");
    if (status === 201) setTimeout(() => setCartState("idle"), 4_000);
  };

  const cartButton =
    cartState === "added" ? (
      <Link href="/cart" className={cn(buttonVariants(), "bg-tint-green text-ink-green hover:bg-tint-green/70 shrink-0")}>
        به سبد اضافه شد · مشاهده سبد
      </Link>
    ) : (
      <button
        type="button"
        disabled={!canAdd}
        onClick={() => void add()}
        className={cn(buttonVariants(), "shrink-0")}
      >
        {!inStock ? "ناموجود" : cartState === "adding" ? "در حال افزودن…" : "افزودن به سبد"}
      </button>
    );

  const stepper = <QuantityStepper value={quantity} onChange={setQuantity} max={Math.min(available, 100)} />;

  return (
    <>
      <section aria-label="قیمت" className="bg-background flex flex-col gap-3 rounded-card p-4 shadow-sm sm:p-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-muted-foreground text-xs">قیمت هر عدد</p>
            <p
              className={cn(
                "mt-1 rounded-md text-2xl font-bold transition-colors duration-700",
                price.halted && "text-muted-foreground text-base font-medium",
                flash && "bg-tint-yellow",
              )}
            >
              {priceLabel(price.priceRial)}
            </p>
          </div>
          <span className={cn("rounded-full px-2.5 py-1 text-xs font-medium", inStock ? "bg-tint-green text-ink-green" : "bg-muted text-muted-foreground")}>
            {inStock ? "موجود" : "ناموجود"}
          </span>
        </div>
        <p className="text-muted-foreground text-xs" aria-live="polite">
          {price.halted ? "قیمت به‌زودی برمی‌گردد؛ خرید تا آن زمان ممکن نیست." : `قیمت لحظه‌ای${updatedAgo ? `، به‌روزرسانی ${updatedAgo}` : ""}`}
        </p>
        <div className="hidden items-center gap-3 sm:flex">
          {inStock ? stepper : null}
          {cartButton}
        </div>
        {cartState === "error" ? <p className="text-ink-red text-xs">افزودن به سبد انجام نشد؛ دوباره تلاش کنید.</p> : null}
      </section>

      {/* Mobile: the price and the cart button stay in reach, just above the tab bar. */}
      <div className="bg-background/95 fixed inset-x-0 z-20 mx-3 flex items-center justify-between gap-3 rounded-header px-4 py-2.5 shadow-md backdrop-blur-sm sm:hidden bottom-[calc(env(safe-area-inset-bottom)+5.25rem)]">
        <span className={cn("text-sm font-bold", price.halted && "text-muted-foreground text-xs font-medium")}>
          {priceLabel(price.priceRial)}
        </span>
        {cartButton}
      </div>
    </>
  );
}
